import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, SelectorContainer, SelectorTitle, SelectorSubtitle, InstanceList, InstanceButton, InstanceIcon, InstanceInfo, InstanceTitle, InstanceId,
} from './ScreenManagerView.styles';
import SocketService from '../../../../packages/shared/socket';
import { ScreenManagerPanel, InterludeOverlay, AdminLogin } from '../components/ScreenManager';
import ScreenControlToggle from '../components/ScreenManager/ScreenControlToggle';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../components/AuthGate';
import { hasRole } from '../utils/roles';
import { canOnInstance } from '../utils/access';
import useAdminInstance from '../hooks/useAdminInstance';
import { getBackendUrl } from '../config/api';

const SCREEN_CODES = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'D1', 'D2'];
const IPAD_CODES = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7', 'P8', 'P9', 'P10'];

const INITIAL_SCREENS = SCREEN_CODES.reduce((acc, code) => {
  acc[code] = {
    isOnline: false,
    currentView: null,
    preview: {},
    pairingCode: null,
  };
  return acc;
}, {});

const INITIAL_IPADS = IPAD_CODES.reduce((acc, code) => {
  acc[code] = {
    isOnline: false,
    currentView: null,
    preview: {},
  };
  return acc;
}, {});

/**
 * ScreenManagerView - Standalone admin page for managing 8 screens
 *
 * Route: /screen-manager?instance=uuid
 *
 * Flow:
 * 1. Check localStorage for saved instance
 * 2. If no saved instance → show AdminLogin
 * 3. After admin PIN login → show instance selector
 * 4. After regular PIN login → use PIN's instance directly
 */
const ScreenManagerView = () => {
  const { instanceId, selectInstance } = useAdminInstance();
  const navigate = useNavigate();

  // Laitteet is a Toteutus page: facilitators run it too, by role or by their
  // grant on the instance (AdminHeader/adminNav.js minRole / instanceAction).
  const { isAuthenticated, isAdmin: authIsAdmin, user } = useAuth();
  const isAdmin = authIsAdmin || hasRole(user, 'facilitator') || canOnInstance(user, instanceId, 'facilitate');

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [screens, setScreens] = useState(INITIAL_SCREENS);
  const [ipads, setIpads] = useState(INITIAL_IPADS);
  const [activeInterlude, setActiveInterlude] = useState(null);
  const [instances, setInstances] = useState([]);
  const [instanceName, setInstanceName] = useState(null);
  const [loadingInstances, setLoadingInstances] = useState(true);

  // Refs
  const socketRef = useRef(null);

  // Redirect participant to admin login
  useEffect(() => {
    if (isAuthenticated && !isAdmin) {
      navigate('/admin');
    }
  }, [isAuthenticated, isAdmin, navigate]);

  // Fetch instances on mount
  useEffect(() => {
    if (!isAdmin) return;

    const fetchInstances = async () => {
      try {
        const socketUrl = getBackendUrl();
        const response = await fetch(`${socketUrl}/api/instances`);
        const data = await response.json();
        setInstances(data);
      } catch (err) {
        console.error('[ScreenManager] Failed to fetch instances:', err);
      } finally {
        setLoadingInstances(false);
      }
    };
    fetchInstances();
  }, [isAdmin]);

  // Handle screen status updates
  const handleScreenStatus = useCallback((data) => {
    console.log('[ScreenManager] Screen status:', data);
    const statusData = {
      isOnline: data.isOnline,
      currentView: data.view,
      preview: data.preview || {},
      pairingCode: data.pairingCode,
    };

    // Check if it's an iPad (P1-P10) or wall screen (A1-D2)
    if (data.code?.startsWith('P')) {
      setIpads((prev) => ({ ...prev, [data.code]: statusData }));
    } else {
      setScreens((prev) => ({ ...prev, [data.code]: statusData }));
    }
  }, []);

  // Handle bulk screen statuses
  const handleScreenStatuses = useCallback((data) => {
    console.log('[ScreenManager] Screen statuses:', data);
    const updatedScreens = { ...INITIAL_SCREENS };
    const updatedIpads = { ...INITIAL_IPADS };

    data.screens?.forEach((screen) => {
      const statusData = {
        isOnline: screen.isOnline,
        currentView: screen.view,
        preview: screen.preview || {},
        pairingCode: screen.pairingCode,
      };

      // Check if it's an iPad (P1-P10) or wall screen (A1-D2)
      if (screen.code?.startsWith('P')) {
        updatedIpads[screen.code] = statusData;
      } else {
        updatedScreens[screen.code] = statusData;
      }
    });

    setScreens(updatedScreens);
    setIpads(updatedIpads);
  }, []);

  // Handle screen state updates (previews)
  const handleScreenStateUpdate = useCallback((data) => {
    const updateFn = (prev) => ({
      ...prev,
      [data.screenId]: {
        ...prev[data.screenId],
        isOnline: true, // If we're getting state updates, it's online
        currentView: data.view,
        preview: data.preview || prev[data.screenId]?.preview,
      },
    });

    // Check if it's an iPad (P1-P10) or wall screen (A1-D2)
    if (data.screenId?.startsWith('P')) {
      setIpads(updateFn);
    } else {
      setScreens(updateFn);
    }
  }, []);

  // Handle interlude state from backend
  const handleInterludeState = useCallback((data) => {
    if (data.active) {
      setActiveInterlude({
        type: data.config.type,
        title: data.config.title,
        subtitle: data.config.subtitle,
        duration: data.config.duration,
        remaining: data.remaining,
        isPaused: data.isPaused,
      });
    } else {
      setActiveInterlude(null);
    }
  }, []);

  // Fetch instance name
  useEffect(() => {
    if (!instanceId) return;

    const socketUrl = getBackendUrl();
    console.log('[ScreenManager] Fetching instance:', instanceId);
    fetch(`${socketUrl}/api/instances/${instanceId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        console.log('[ScreenManager] Instance data:', data);
        setInstanceName(data.name || data.title || instanceId.slice(0, 8));
      })
      .catch((err) => {
        console.error('[ScreenManager] Failed to fetch instance:', err);
        setInstanceName(instanceId.slice(0, 8) + '...');
      });
  }, [instanceId]);

  // Connect to socket
  useEffect(() => {
    if (!instanceId) return;

    console.log(`[ScreenManager] Connecting for instance ${instanceId}`);

    const socket = SocketService.connect(instanceId, 'admin', 'screen-manager');
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[ScreenManager] Connected');
      setIsConnected(true);

      // Request current screen statuses
      socket.emit('screen-status-request', { instanceId });
    });

    socket.on('disconnect', () => {
      console.log('[ScreenManager] Disconnected');
      setIsConnected(false);
    });

    // Screen events
    socket.on('screen-status', handleScreenStatus);
    socket.on('screen-statuses', handleScreenStatuses);
    socket.on('screen-state-update', handleScreenStateUpdate);
    socket.on('screen-connected', handleScreenStatus);
    socket.on('screen-disconnected', handleScreenStatus);

    // Interlude events
    socket.on('interlude-state', handleInterludeState);
    socket.on('interlude-tick', (data) => {
      setActiveInterlude((prev) => prev ? { ...prev, remaining: data.remaining } : null);
    });
    socket.on('interlude-complete', () => {
      setActiveInterlude(null);
    });

    return () => {
      console.log('[ScreenManager] Cleaning up');
      socket.off('screen-status', handleScreenStatus);
      socket.off('screen-statuses', handleScreenStatuses);
      socket.off('screen-state-update', handleScreenStateUpdate);
      socket.off('screen-connected', handleScreenStatus);
      socket.off('screen-disconnected', handleScreenStatus);
      socket.off('interlude-state', handleInterludeState);
      // Don't disconnect - socket is a shared singleton
    };
  }, [instanceId, handleScreenStatus, handleScreenStatuses, handleScreenStateUpdate, handleInterludeState]);

  // Actions
  const handleScreenSelect = (code) => {
    console.log('[ScreenManager] Screen selected:', code);
  };

  const handleScreenViewChange = (code, view) => {
    console.log('[ScreenManager] Changing screen view:', code, view);
    socketRef.current?.emit('screen-command', {
      targetScreen: code,
      command: 'switch-view',
      payload: { view },
      instanceId, // Include instanceId for persistence
    });

    // Optimistic update
    setScreens((prev) => ({
      ...prev,
      [code]: { ...prev[code], currentView: view },
    }));
  };

  const handleInterludeStart = (config) => {
    console.log('[ScreenManager] Starting interlude:', config);
    socketRef.current?.emit('interlude-start', {
      instanceId,
      config,
      saveStates: true,
    });

    // Optimistic update - backend will send tick events
    setActiveInterlude({
      ...config,
      remaining: config.duration,
      isPaused: false,
    });
  };

  const handleInterludePause = () => {
    console.log('[ScreenManager] Pausing interlude');
    socketRef.current?.emit('interlude-pause', { instanceId });
    setActiveInterlude((prev) => prev ? { ...prev, isPaused: true } : null);
  };

  const handleInterludeResume = () => {
    console.log('[ScreenManager] Resuming interlude');
    socketRef.current?.emit('interlude-resume', { instanceId });
    setActiveInterlude((prev) => prev ? { ...prev, isPaused: false } : null);
  };

  const handleInterludeEnd = () => {
    console.log('[ScreenManager] Ending interlude');
    socketRef.current?.emit('interlude-end', { instanceId, restore: true });
    setActiveInterlude(null);
  };

  const handleSyncAll = () => {
    console.log('[ScreenManager] Syncing all screens');
    socketRef.current?.emit('screen-status-request', { instanceId });
  };

  const handleSetAllView = (view) => {
    console.log('[ScreenManager] Setting all screens to:', view);
    SCREEN_CODES.forEach((code) => {
      if (screens[code].isOnline) {
        handleScreenViewChange(code, view);
      }
    });
  };

  // iPad handlers
  const handleiPadSelect = (code) => {
    console.log('[ScreenManager] iPad selected:', code);
  };

  const handleiPadViewChange = (code, view) => {
    console.log('[ScreenManager] Changing iPad view:', code, view);
    socketRef.current?.emit('screen-command', {
      targetScreen: code,
      command: 'switch-view',
      payload: { view },
      instanceId, // Include instanceId for persistence
    });

    // Optimistic update
    setIpads((prev) => ({
      ...prev,
      [code]: { ...prev[code], currentView: view },
    }));
  };

  if (!isAdmin) {
    return null;
  }

  // Show instance selector if no instance is selected
  if (!instanceId) {
    return (
      <SelectorContainer>
        <SelectorTitle>Screen Manager</SelectorTitle>
        <SelectorSubtitle>Valitse instanssi</SelectorSubtitle>

        {loadingInstances ? (
          <div style={{ color: '#666' }}>Ladataan...</div>
        ) : instances.length === 0 ? (
          <div style={{ color: '#666' }}>Ei instansseja saatavilla</div>
        ) : (
          <InstanceList>
            {instances.map((inst) => (
              <InstanceButton
                key={inst.id}
                onClick={() => {
                  localStorage.setItem('kuutio-instance', inst.id);
                  selectInstance(inst.id);
                }}
              >
                <InstanceIcon>🎯</InstanceIcon>
                <InstanceInfo>
                  <InstanceTitle>{inst.title || inst.name}</InstanceTitle>
                  <InstanceId>{inst.id}</InstanceId>
                </InstanceInfo>
              </InstanceButton>
            ))}
          </InstanceList>
        )}
      </SelectorContainer>
    );
  }

  return (
    <Container>
      <AdminHeader
        instanceId={instanceId}
        instanceName={instanceName}
      />
      <ScreenControlToggle instanceId={instanceId} />

      <ScreenManagerPanel
        screens={screens}
        ipads={ipads}
        activeInterlude={activeInterlude}
        instanceId={instanceId}
        onScreenSelect={handleScreenSelect}
        onScreenViewChange={handleScreenViewChange}
        oniPadSelect={handleiPadSelect}
        oniPadViewChange={handleiPadViewChange}
        onInterludeStart={handleInterludeStart}
        onInterludePause={handleInterludePause}
        onInterludeResume={handleInterludeResume}
        onInterludeEnd={handleInterludeEnd}
        onSyncAll={handleSyncAll}
        onSetAllView={handleSetAllView}
      />
    </Container>
  );
};

export default ScreenManagerView;
